import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import TutorCard from "./components/TutorCard";
import { toId } from "../lib/subjects";
import { createTaskBroadcast } from "../data/requests";
import { toast } from "./components/ToastHost";
import { ALL_SUBJECT_KEYS, SUBJECT_GROUP_ORDER, SUBJECTS_BY_GROUP } from "../lib/subjectCatalog";
import { DEGREE_KEYS, TITLE_KEYS, EXPERIENCE_RANGES, credentialFlags } from "../lib/academic";
import { auth as fbAuth } from "../firebase";

/* ---------------- Icons ---------------- */
function MagnifierIcon({ className = "h-5 w-5" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
      <path
        d="M21 21l-3.6-3.6m-1.8-7.2a7.2 7.2 0 11-14.4 0 7.2 7.2 0 0114.4 0z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function FilterIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="7" y1="12" x2="17" y2="12" />
      <line x1="10" y1="18" x2="14" y2="18" />
    </svg>
  );
}

/* Sphere (main category) select — 17 top-level fields */
function SphereSelect({ value, onChange, t, className, placeholder }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className={className}>
      <option value="">{placeholder ?? t("search.filters.sphere")}</option>
      {SUBJECT_GROUP_ORDER.map((gid) => (
        <option key={gid} value={gid}>
          {t(`subjectGroups.${gid}`)}
        </option>
      ))}
    </select>
  );
}

/* Subject select — filtered to the chosen sphere (or all if no sphere) */
function SubjectSelect({ value, onChange, sphere, t, extra, className }) {
  const keys = sphere && SUBJECTS_BY_GROUP[sphere]
    ? SUBJECTS_BY_GROUP[sphere]
    : ALL_SUBJECT_KEYS;

  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className={className}>
      <option value="">{t("search.filters.specialization")}</option>
      {sphere ? (
        keys.map((s) => (
          <option key={s} value={s}>
            {t(`subjects.${s}`, { defaultValue: s })}
          </option>
        ))
      ) : (
        SUBJECT_GROUP_ORDER.map((gid) => (
          <optgroup key={gid} label={t(`subjectGroups.${gid}`)}>
            {(SUBJECTS_BY_GROUP[gid] || []).map((s) => (
              <option key={s} value={s}>
                {t(`subjects.${s}`, { defaultValue: s })}
              </option>
            ))}
          </optgroup>
        ))
      )}
      {extra.length > 0 && !sphere ? (
        <optgroup label={t("subjectGroups.other")}>
          {extra
            .slice()
            .sort((a, b) =>
              t(`subjects.${toId(a)}`, { defaultValue: a }).localeCompare(
                t(`subjects.${toId(b)}`, { defaultValue: b })
              )
            )
            .map((s) => (
              <option key={s} value={s}>
                {t(`subjects.${toId(s)}`, { defaultValue: s })}
              </option>
            ))}
        </optgroup>
      ) : null}
    </select>
  );
}

/* ---------------- helpers ---------------- */
const API_BASE =
  process.env.REACT_APP_API_URL ||
  "http://localhost:3001";

const absUrl = (u) => {
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  return `${API_BASE}${u.startsWith("/") ? "" : "/"}${u}`;
};

const numOrEmpty = (v) => {
  const n = Number(String(v ?? "").replace(",", "."));
  return Number.isFinite(n) ? n : "";
};

const intOrNull = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(String(v).replace(",", "."));
  if (!Number.isFinite(n)) return null;
  return Math.trunc(n);
};

const floatOrNull = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(String(v).replace(",", "."));
  if (!Number.isFinite(n)) return null;
  return n;
};

/* ---------------- small UI helper ---------------- */
function Field({ label, children }) {
  return (
    <div>
      <div className="mb-1.5 text-sm text-white/70">{label}</div>
      {children}
    </div>
  );
}

function Pill({ children, color = "#7269e3" }) {
  return (
    <span
      className="inline-flex items-center rounded-full px-3 py-1 text-sm font-semibold ring-1"
      style={{
        background: `${color}20`,
        color,
        borderColor: `${color}55`,
      }}
    >
      {children}
    </span>
  );
}

function SkeletonCard() {
  return (
    <div className="animate-pulse rounded-3xl bg-white/5 p-5 ring-1 ring-white/10">
      <div className="flex items-start gap-4">
        <div className="h-16 w-16 rounded-2xl bg-white/10" />
        <div className="flex-1">
          <div className="h-4 w-2/3 rounded bg-white/10" />
          <div className="mt-2 h-3 w-1/3 rounded bg-white/10" />
          <div className="mt-4 flex gap-2">
            <div className="h-6 w-20 rounded-full bg-white/10" />
            <div className="h-6 w-24 rounded-full bg-white/10" />
          </div>
          <div className="mt-5 h-9 w-32 rounded-xl bg-white/10" />
        </div>
      </div>
    </div>
  );
}

/* Search input with a specialization-autocomplete dropdown. Suggestions are
 * drawn only from the platform's single canonical specialization directory
 * (subjectCatalog.js + the subjects/subjectGroups translations) — the same
 * source the Sphere/Subject <select>s below use — so results are identical
 * for every user, logged in or not, and never include invented categories. */
function SpecializationSearchInput({
  value,
  onChange,
  suggestions,
  open,
  onFocus,
  onBlur,
  onSelect,
  placeholder,
  t,
  inputClassName,
  wrapperClassName = "",
}) {
  return (
    <div className={`relative ${wrapperClassName}`}>
      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-white/60">
        <MagnifierIcon className="h-5 w-5" />
      </span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder={placeholder}
        autoComplete="off"
        className={inputClassName}
      />

      {/* Only show the suggestions dropdown when there are actual specialization
          matches — a free-text keyword search (e.g. a word from a bio) still
          filters the results below, so showing a "nothing found" box here was
          misleading when results clearly existed. */}
      {open && value.trim() && suggestions.length > 0 && (
        <div className="absolute left-0 right-0 top-full z-30 mt-1.5 overflow-hidden rounded-2xl border border-white/10 bg-[#1f2430] shadow-2xl">
          <ul className="max-h-64 overflow-y-auto py-1">
            {suggestions.map((item) => (
              <li key={`${item.type}-${item.id}`}>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    // mousedown (not click) fires before the input's onBlur,
                    // so the selection registers before the dropdown closes
                    e.preventDefault();
                    onSelect(item);
                  }}
                  className="flex w-full items-center justify-between gap-2 px-4 py-2.5 text-left text-sm text-white/90 transition hover:bg-white/10"
                >
                  <span className={item.type === "sphere" ? "font-semibold" : ""}>
                    {item.label}
                  </span>
                  {item.type === "subject" && (
                    <span className="shrink-0 text-xs text-white/40">
                      {t(`subjectGroups.${item.group}`)}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function Search() {
  const { t } = useTranslation();

  // Auth: initialise synchronously from localStorage, then sync with Firebase
  // (same pattern as TutorProfilePublic.jsx) so a guest never briefly sees the
  // real "create request" form before the gate kicks in.
  const [auth, setAuth] = useState(() => {
    try { return JSON.parse(localStorage.getItem("auth") || "null"); }
    catch { return null; }
  });

  useEffect(() => {
    const unsub = fbAuth.onAuthStateChanged((fbUser) => {
      if (!fbUser) {
        localStorage.removeItem("auth");
        localStorage.removeItem("isAuthed");
        setAuth(null);
        return;
      }
      const isEmailProvider = fbUser.providerData.some((p) => p.providerId === "password");
      if (isEmailProvider && !fbUser.emailVerified) {
        localStorage.removeItem("auth");
        localStorage.removeItem("isAuthed");
        setAuth(null);
        return;
      }
      try { setAuth(JSON.parse(localStorage.getItem("auth") || "null")); }
      catch { setAuth(null); }
    });
    return () => unsub();
  }, []);

  // Shown instead of the real broadcast-request modal when a guest clicks
  // "Створити запит" — prevents an unregistered visitor from filling out the
  // whole form only to be rejected by the backend at submit time.
  const [showGuestGate, setShowGuestGate] = useState(false);

  const [query, setQuery] = useState("");
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [sphere, setSphere] = useState("");
  const [subject, setSubject] = useState("");
  const [format, setFormat] = useState("");
  const [gender, setGender] = useState("");
  const [minAge, setMinAge] = useState("");
  const [maxAge, setMaxAge] = useState("");
  const [minRating, setMinRating] = useState("");
  const [maxRate, setMaxRate] = useState("");
  const [degree, setDegree] = useState("");
  const [academicTitle, setAcademicTitle] = useState("");
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [expRange, setExpRange] = useState("");
  const [sortBy, setSortBy] = useState("default");

  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sending, setSending] = useState(false);

  const [taskBudget, setTaskBudget] = useState("");
  const [taskMessage, setTaskMessage] = useState("");

  const [reqSphere, setReqSphere] = useState("");
  const [reqSubject, setReqSubject] = useState("");
  const [reqFormat, setReqFormat] = useState("online"); // platform is online-only; setter kept for future use
  const [reqGender, setReqGender] = useState("");
  const [reqMinAge, setReqMinAge] = useState("");
  const [reqMaxAge, setReqMaxAge] = useState("");
  const [reqMinRating, setReqMinRating] = useState("");

  useEffect(() => {
    let alive = true;

    (async () => {
      setLoading(true);
      setLoadError("");

      try {
        const res = await fetch(`${API_BASE}/api/tutors?limit=50`);
        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          throw new Error("LOAD_TUTORS_FAILED");
        }

        const rawItems = Array.isArray(data) ? data : data?.items ?? [];

        const mapped = rawItems.map((u) => ({
          id: u.id,
          name: u.profile?.displayName || u.name || t("search.defaultTutorName"),
          avatar: absUrl(u.profile?.avatarUrl) || "",
          location: u.profile?.city || "",
          subjects: Array.isArray(u.profile?.subjects) ? u.profile.subjects : [],
          sphere: u.profile?.sphere || "",
          rate: u.profile?.rate,
          currency: u.profile?.currency || "UAH",
          rating: u.profile?.rating,
          gender: u.profile?.gender || "",
          birthYear: u.profile?.birthYear ?? null,
          about: u.profile?.bio || "",
          degree: u.profile?.degree || "",
          academicTitle: u.profile?.academicTitle || "",
          experienceYears:
            typeof u.profile?.experienceYears === "number" ? u.profile.experienceYears : null,
          isVerified: !!u.isVerified,
          ...credentialFlags(u.profile, u.isVerified),
        }));

        if (alive) setTutors(mapped);
      } catch {
        if (alive) {
          setLoadError(t("search.errors.loadTutors"));
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [t]);

  const subjectSelectGroups = useMemo(() => {
    const fromTutors = new Set();
    tutors.forEach((tu) => (tu.subjects || []).forEach((s) => s && fromTutors.add(String(s))));
    const catalogSet = new Set(ALL_SUBJECT_KEYS);
    const extra = Array.from(fromTutors).filter((s) => !catalogSet.has(s));
    return { extra };
  }, [tutors]);

  // Single canonical directory of real spheres + specializations (same data
  // the Sphere/Subject <select>s use) — the autocomplete below only ever
  // suggests entries from this list, so guests and authed users alike see
  // identical, never-invented results.
  const specializationDirectory = useMemo(() => {
    const items = [];
    SUBJECT_GROUP_ORDER.forEach((gid) => {
      items.push({ type: "sphere", id: gid, group: gid, label: t(`subjectGroups.${gid}`) });
      (SUBJECTS_BY_GROUP[gid] || []).forEach((sid) => {
        items.push({
          type: "subject",
          id: sid,
          group: gid,
          label: t(`subjects.${sid}`, { defaultValue: sid }),
        });
      });
    });
    return items;
  }, [t]);

  const specializationSuggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return specializationDirectory
      .filter((item) => item.label.toLowerCase().includes(q))
      .sort((a, b) => {
        const aStarts = a.label.toLowerCase().startsWith(q) ? 0 : 1;
        const bStarts = b.label.toLowerCase().startsWith(q) ? 0 : 1;
        if (aStarts !== bStarts) return aStarts - bStarts;
        // Broad sphere names (e.g. "Психологія") rank above the dozens of
        // specific specializations that share the same first letter, so a
        // single typed letter still surfaces the obvious top-level match
        // instead of it being buried among narrower subject entries.
        const aType = a.type === "sphere" ? 0 : 1;
        const bType = b.type === "sphere" ? 0 : 1;
        if (aType !== bType) return aType - bType;
        return a.label.localeCompare(b.label);
      })
      .slice(0, 8);
  }, [specializationDirectory, query]);

  const selectSpecializationSuggestion = (item) => {
    if (item.type === "sphere") {
      setSphere(item.id);
      setSubject("");
    } else {
      setSphere(item.group);
      setSubject(item.id);
    }
    // The pick is now reflected in the Sphere/Subject selects below, so the
    // free-text box is cleared rather than left holding a label that would
    // otherwise also be (mis)applied as a literal name/location text filter.
    setQuery("");
    setSuggestOpen(false);
  };

  const results = useMemo(() => {
    const year = new Date().getFullYear();

    return tutors.filter((tu) => {
      const q = query.trim().toLowerCase();

      // Keyword search across the WHOLE profile, including the free-text bio/
      // description — so a client who doesn't know the exact specialization can
      // type keywords from their head ("готую до НМТ", "гештальт", "стартап")
      // and still find a specialist whose profile mentions them. Subjects are
      // stored as raw catalog keys, so we also include each subject's translated
      // label and the sphere label, making search work in the UI language even
      // without opening the autocomplete. Multiple words are matched with AND:
      // every typed word must appear somewhere in the profile text.
      if (q) {
        const haystack = [
          tu.name,
          tu.location,
          tu.about,
          tu.sphere ? t(`subjectGroups.${tu.sphere}`, { defaultValue: tu.sphere }) : "",
          ...(tu.subjects || []).map((s) => String(s || "")),
          ...(tu.subjects || []).map((s) => t(`subjects.${toId(s)}`, { defaultValue: s })),
        ]
          .join(" ")
          .toLowerCase();

        const tokens = q.split(/\s+/).filter(Boolean);
        const matchesQuery = tokens.every((tok) => haystack.includes(tok));
        if (!matchesQuery) return false;
      }

      // Filter by main sphere — match stored sphere field OR derive from subjects
      if (sphere) {
        const sphereKeys = new Set(SUBJECTS_BY_GROUP[sphere] || []);
        const matchesSphere =
          (tu.sphere && tu.sphere === sphere) ||
          (tu.subjects || []).some((s) => sphereKeys.has(String(s)));
        if (!matchesSphere) return false;
      }

      // Filter by specific subject within sphere
      if (subject) {
        const has = (tu.subjects || []).some((s) => String(s) === String(subject));
        if (!has) return false;
      }

      if (format) {
        // reserved for future backend field
      }

      if (gender && String(tu.gender || "") !== gender) return false;

      if (minRating) {
        const r = typeof tu.rating === "number" ? tu.rating : null;
        if (r === null || r < Number(minRating)) return false;
      }

      if (maxRate) {
        const r = typeof tu.rate === "number" ? tu.rate : null;
        if (r === null || r > Number(maxRate)) return false;
      }

      if (minAge && typeof tu.birthYear === "number") {
        const age = year - tu.birthYear;
        if (age < Number(minAge)) return false;
      }

      if (maxAge && typeof tu.birthYear === "number") {
        const age = year - tu.birthYear;
        if (age > Number(maxAge)) return false;
      }

      if (verifiedOnly && !tu.fullyVerified) return false;
      if (degree && String(tu.degree || "") !== degree) return false;
      if (academicTitle && String(tu.academicTitle || "") !== academicTitle) return false;

      if (expRange) {
        const r = EXPERIENCE_RANGES.find((x) => x.id === expRange);
        const e = typeof tu.experienceYears === "number" ? tu.experienceYears : null;
        if (!r || e === null) return false;
        if (e < r.min) return false;
        if (r.max !== undefined && e >= r.max) return false;
      }

      return true;
    }).sort((a, b) => {
      if (sortBy === "ratingDesc") {
        const ra = typeof a.rating === "number" ? a.rating : -1;
        const rb = typeof b.rating === "number" ? b.rating : -1;
        return rb - ra;
      }
      if (sortBy === "priceAsc") {
        const pa = typeof a.rate === "number" ? a.rate : Infinity;
        const pb = typeof b.rate === "number" ? b.rate : Infinity;
        return pa - pb;
      }
      if (sortBy === "priceDesc") {
        const pa = typeof a.rate === "number" ? a.rate : -1;
        const pb = typeof b.rate === "number" ? b.rate : -1;
        return pb - pa;
      }
      if (sortBy === "nameAsc") {
        return (a.name || "").localeCompare(b.name || "");
      }
      return 0;
    });
  }, [tutors, query, sphere, subject, format, gender, minAge, maxAge, minRating, maxRate, degree, academicTitle, expRange, verifiedOnly, sortBy, t]);

  const previewRecipients = useMemo(() => {
    const year = new Date().getFullYear();

    return tutors.filter((tu) => {
      if (reqSphere) {
        const sphereKeys = new Set(SUBJECTS_BY_GROUP[reqSphere] || []);
        const matchesSphere =
          (tu.sphere && tu.sphere === reqSphere) ||
          (tu.subjects || []).some((s) => sphereKeys.has(String(s)));
        if (!matchesSphere) return false;
      }

      if (reqSubject) {
        const has = (tu.subjects || []).some((s) => String(s) === String(reqSubject));
        if (!has) return false;
      }

      if (reqGender && String(tu.gender || "") !== reqGender) return false;

      if (reqMinRating) {
        const r = typeof tu.rating === "number" ? tu.rating : null;
        if (r === null || r < Number(reqMinRating)) return false;
      }

      if (reqMinAge) {
        if (typeof tu.birthYear === "number") {
          const age = year - tu.birthYear;
          if (age < Number(reqMinAge)) return false;
        } else {
          return false;
        }
      }

      if (reqMaxAge) {
        if (typeof tu.birthYear === "number") {
          const age = year - tu.birthYear;
          if (age > Number(reqMaxAge)) return false;
        } else {
          return false;
        }
      }

      return true;
    });
  }, [tutors, reqSphere, reqSubject, reqGender, reqMinAge, reqMaxAge, reqMinRating]);

  const openModal = () => {
    if (!auth) {
      setShowGuestGate(true);
      return;
    }
    setReqSphere(sphere || "");
    setReqSubject(subject || "");
    setReqFormat("online"); // platform is online-only
    setReqGender(gender || "");
    setReqMinAge(minAge || "");
    setReqMaxAge(maxAge || "");
    setReqMinRating(minRating || "");
    setShowModal(true);
  };

  const resetFilters = () => {
    setQuery("");
    setSphere("");
    setSubject("");
    setFormat("");
    setGender("");
    setMinAge("");
    setMaxAge("");
    setMinRating("");
    setMaxRate("");
    setDegree("");
    setAcademicTitle("");
    setExpRange("");
    setVerifiedOnly(false);
    setSortBy("default");
  };

  const submitBroadcast = async () => {
    const b = numOrEmpty(taskBudget);
    if (!b || b <= 0) {
      toast(t("search.broadcast.errors.invalidBudget"), "error");
      return;
    }

    const minAgeValue = intOrNull(reqMinAge);
    const maxAgeValue = intOrNull(reqMaxAge);

    if (minAgeValue !== null && maxAgeValue !== null && minAgeValue > maxAgeValue) {
      toast(t("search.broadcast.errors.invalidAgeRange"), "error");
      return;
    }

    const msg = String(taskMessage || "").trim();
    if (!msg) {
      toast(t("search.broadcast.errors.emptyMessage"), "error");
      return;
    }

    setSending(true);

    try {
      const payload = {
        subject: reqSubject || reqSphere || "General",
        mode: reqFormat || "any",
        budget: b,
        message: msg,
        filters: {
          gender: reqGender || undefined,
          ageMin: minAgeValue ?? undefined,
          ageMax: maxAgeValue ?? undefined,
          minRating: floatOrNull(reqMinRating) ?? undefined,
        },
      };

      const res = await createTaskBroadcast(payload);

      setShowModal(false);
      setTaskMessage("");
      setTaskBudget("");

      const matched = Number(res?.matched);
      const created = Number(res?.created);

      toast(
        t("search.broadcast.success", {
          matched: Number.isFinite(matched) ? matched : 0,
          created: Number.isFinite(created) ? created : 0,
        }),
        "success"
      );
    } catch (e) {
      // Surface the real reason instead of always showing the generic
      // message — makes failures (rate limit, validation, auth, etc.)
      // actually diagnosable instead of silently hiding the cause.
      console.error("createTaskBroadcast failed:", e?.status, e?.data || e?.message);

      const code =
        (e?.data && typeof e.data === "object" && e.data.error) ||
        (typeof e?.message === "string" ? e.message : "");

      const codeKeyMap = {
        TOO_FAST: "tooFast",
        TOO_MANY_BROADCASTS: "tooMany",
        FORBIDDEN: "forbidden",
        USER_NOT_FOUND: "authRequired",
        SUBJECT_REQUIRED: "invalidBudget",
        INVALID_BUDGET: "invalidBudget",
        EMPTY_MESSAGE: "emptyMessage",
      };

      const key = codeKeyMap[code];
      toast(
        key
          ? t(`search.broadcast.errors.${key}`)
          : t("search.broadcast.errors.failed"),
        "error"
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#272c39] text-white">
      <div className="mx-auto max-w-6xl px-4 pb-24 pt-6 md:pt-10">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between md:gap-4">
          <div>
            <h1 className="font-unbounded text-2xl font-extrabold tracking-tight md:text-4xl">
              {t("search.title")}
            </h1>

            <p className="mt-1.5 max-w-xl text-sm text-white/60 md:mt-2 md:text-base">
              {t("search.subtitle")}
            </p>

            <div className="mt-3 hidden items-center gap-2 sm:flex md:mt-4">
              <Pill color="#98dea3">{t("search.tip1")}</Pill>
              <Pill color="#7269e3">{t("search.tip2")}</Pill>
              <Pill color="#a783a6">{t("search.tip3")}</Pill>
            </div>
          </div>

          <button
            onClick={openModal}
            className="w-full rounded-2xl bg-[#7269e3] px-5 py-3 font-semibold shadow-lg shadow-[#7269e3]/30 transition hover:-translate-y-0.5 hover:shadow-[#7269e3]/50 md:w-auto"
          >
            {t("search.createRequest")}
          </button>
        </div>

        {/* ── Filter bar ──────────────────────────────────────────── */}
        <div className="mt-5 rounded-3xl bg-white/5 p-3 ring-1 ring-white/10 md:mt-7 md:p-4">

          {/* ── DESKTOP filter grid ── */}
          <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-3">
            <SpecializationSearchInput
              value={query}
              onChange={(v) => { setQuery(v); setSuggestOpen(!!v.trim()); }}
              suggestions={specializationSuggestions}
              open={suggestOpen}
              onFocus={() => query.trim() && setSuggestOpen(true)}
              onBlur={() => setTimeout(() => setSuggestOpen(false), 120)}
              onSelect={selectSpecializationSuggestion}
              placeholder={t("search.filters.search")}
              t={t}
              wrapperClassName="col-span-2"
              inputClassName="w-full rounded-2xl border border-white/10 bg-white/10 py-3 pl-10 pr-3 outline-none placeholder:text-white/40 focus:ring-2 focus:ring-[#a783a6]"
            />

            {/* Step 1: Sphere */}
            <SphereSelect
              value={sphere}
              onChange={(v) => { setSphere(v); setSubject(""); }}
              t={t}
              className="rounded-2xl border border-white/10 bg-white/10 px-3 py-3 outline-none focus:ring-2 focus:ring-[#a783a6]"
            />

            {/* Step 2: Specific subject (only shown when sphere is selected) */}
            {sphere ? (
              <SubjectSelect
                value={subject}
                onChange={setSubject}
                sphere={sphere}
                t={t}
                extra={subjectSelectGroups.extra}
                className="rounded-2xl border border-white/10 bg-white/10 px-3 py-3 outline-none focus:ring-2 focus:ring-[#a783a6]"
              />
            ) : (
              <div className="hidden lg:block" />
            )}

            <select value={gender} onChange={(e) => setGender(e.target.value)} className="rounded-2xl border border-white/10 bg-white/10 px-3 py-3 outline-none focus:ring-2 focus:ring-[#a783a6]">
              <option value="">{t("search.filters.gender")}</option>
              <option value="female">{t("search.filters.female")}</option>
              <option value="male">{t("search.filters.male")}</option>
              <option value="other">{t("search.filters.other")}</option>
            </select>

            <select value={minRating} onChange={(e) => setMinRating(e.target.value)} className="rounded-2xl border border-white/10 bg-white/10 px-3 py-3 outline-none focus:ring-2 focus:ring-[#a783a6]">
              <option value="">{t("search.filters.ratingFrom")}</option>
              <option value="4">4+</option>
              <option value="4.5">4.5+</option>
              <option value="4.9">4.9+</option>
            </select>

            <input type="number" min="0" value={maxRate} onChange={(e) => setMaxRate(e.target.value)} placeholder={t("search.filters.maxPrice")} className="rounded-2xl border border-white/10 bg-white/10 px-3 py-3 outline-none placeholder:text-white/40 focus:ring-2 focus:ring-[#a783a6]" />

            <select value={degree} onChange={(e) => setDegree(e.target.value)} className="rounded-2xl border border-white/10 bg-white/10 px-3 py-3 outline-none focus:ring-2 focus:ring-[#a783a6]">
              <option value="">{t("academic.degreeLabel")}</option>
              {DEGREE_KEYS.map((k) => <option key={k} value={k}>{t(`academic.degree.${k}`)}</option>)}
            </select>

            <select value={academicTitle} onChange={(e) => setAcademicTitle(e.target.value)} className="rounded-2xl border border-white/10 bg-white/10 px-3 py-3 outline-none focus:ring-2 focus:ring-[#a783a6]">
              <option value="">{t("academic.titleLabel")}</option>
              {TITLE_KEYS.map((k) => <option key={k} value={k}>{t(`academic.title.${k}`)}</option>)}
            </select>

            <select value={expRange} onChange={(e) => setExpRange(e.target.value)} className="rounded-2xl border border-white/10 bg-white/10 px-3 py-3 outline-none focus:ring-2 focus:ring-[#a783a6]">
              <option value="">{t("academic.experienceLabel")}</option>
              {EXPERIENCE_RANGES.map((r) => <option key={r.id} value={r.id}>{t(`academic.expRange.${r.id}`)}</option>)}
            </select>

            <button
              type="button"
              aria-pressed={verifiedOnly}
              onClick={() => setVerifiedOnly((v) => !v)}
              className={
                "rounded-2xl border px-4 py-3 text-sm transition " +
                (verifiedOnly
                  ? "border-[#98dea3]/40 bg-[#98dea3]/15 text-[#98dea3]"
                  : "border-white/10 bg-white/10 text-white/80 hover:bg-white/[0.14]")
              }
            >
              {verifiedOnly ? "✓ " : ""}{t("credentials.filterVerifiedOnly")}
            </button>

            <button type="button" onClick={resetFilters} className="rounded-2xl border border-white/15 bg-white/[0.04] px-4 py-3 text-sm text-white/80 transition hover:bg-white/[0.07]">
              {t("search.reset")}
            </button>
          </div>

          {/* ── MOBILE compact bar ── */}
          <div className="flex flex-col gap-2 md:hidden">
            {/* Search input */}
            <SpecializationSearchInput
              value={query}
              onChange={(v) => { setQuery(v); setSuggestOpen(!!v.trim()); }}
              suggestions={specializationSuggestions}
              open={suggestOpen}
              onFocus={() => query.trim() && setSuggestOpen(true)}
              onBlur={() => setTimeout(() => setSuggestOpen(false), 120)}
              onSelect={selectSpecializationSuggestion}
              placeholder={t("search.filters.search")}
              t={t}
              inputClassName="w-full rounded-2xl border border-white/10 bg-white/10 py-3 pl-10 pr-3 outline-none placeholder:text-white/40 focus:ring-2 focus:ring-[#a783a6]"
            />

            {/* Sphere + Filters button row */}
            <div className="flex gap-2">
              <SphereSelect
                value={sphere}
                onChange={(v) => { setSphere(v); setSubject(""); }}
                t={t}
                className="min-w-0 flex-1 rounded-2xl border border-white/10 bg-white/10 px-3 py-3 outline-none focus:ring-2 focus:ring-[#a783a6]"
              />
              <button
                type="button"
                onClick={() => setFiltersOpen(true)}
                className="relative flex shrink-0 items-center gap-1.5 rounded-2xl border border-white/15 bg-white/[0.06] px-4 py-3 text-sm font-medium text-white/80 transition active:bg-white/10"
              >
                <FilterIcon />
                <span>{t("search.filters.more", { defaultValue: "Filters" })}</span>
                {[subject, gender, minRating, maxRate].filter(Boolean).length > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#7269e3] px-1 text-[10px] font-bold text-white">
                    {[subject, gender, minRating, maxRate].filter(Boolean).length}
                  </span>
                )}
              </button>
            </div>

            {/* Sort + count row */}
            <div className="flex items-center justify-between gap-2 pt-0.5">
              <span className="text-sm text-white/55">{t("search.found", { count: results.length })}</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="rounded-xl border border-white/10 bg-white/10 px-3 py-1.5 text-sm text-white/80 outline-none focus:ring-2 focus:ring-[#a783a6]"
              >
                <option value="default">{t("search.sortOptions.default")}</option>
                <option value="ratingDesc">{t("search.sortOptions.ratingDesc")}</option>
                <option value="priceAsc">{t("search.sortOptions.priceAsc")}</option>
                <option value="priceDesc">{t("search.sortOptions.priceDesc")}</option>
                <option value="nameAsc">{t("search.sortOptions.nameAsc")}</option>
              </select>
            </div>
          </div>

          {/* Desktop: found count + sort */}
          <div className="mt-4 hidden items-center justify-between gap-3 md:flex">
            <div className="text-sm text-white/65">{t("search.found", { count: results.length })}</div>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="rounded-xl border border-white/10 bg-white/10 px-3 py-1.5 text-sm text-white/80 outline-none focus:ring-2 focus:ring-[#a783a6]"
            >
              <option value="default">{t("search.sortOptions.default")}</option>
              <option value="ratingDesc">{t("search.sortOptions.ratingDesc")}</option>
              <option value="priceAsc">{t("search.sortOptions.priceAsc")}</option>
              <option value="priceDesc">{t("search.sortOptions.priceDesc")}</option>
              <option value="nameAsc">{t("search.sortOptions.nameAsc")}</option>
            </select>
          </div>
        </div>

        {/* ── Mobile filter drawer ─────────────────────────────────── */}
        {filtersOpen && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-40 bg-black/60 md:hidden"
              onClick={() => setFiltersOpen(false)}
            />
            {/* Slide-up panel */}
            <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden rounded-t-3xl bg-[#1f2430] shadow-2xl ring-1 ring-white/10">
              <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
                <span className="text-lg font-semibold">{t("search.filters.more", { defaultValue: "Filters" })}</span>
                <button
                  onClick={() => setFiltersOpen(false)}
                  className="rounded-xl bg-white/10 p-2 text-white/70 hover:bg-white/15"
                >
                  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>

              <div className="space-y-4 overflow-y-auto p-5" style={{ maxHeight: "60vh" }}>
                {/* Specific specialization (only if sphere is selected) */}
                {sphere && (
                  <div>
                    <div className="mb-1.5 text-sm text-white/70">{t("search.filters.specialization")}</div>
                    <SubjectSelect
                      value={subject}
                      onChange={setSubject}
                      sphere={sphere}
                      t={t}
                      extra={[]}
                      className="w-full rounded-2xl border border-white/10 bg-white/10 px-4 py-3 outline-none focus:ring-2 focus:ring-[#a783a6]"
                    />
                  </div>
                )}

                <div>
                  <div className="mb-1.5 text-sm text-white/70">{t("search.filters.gender")}</div>
                  <select
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                    className="w-full rounded-2xl border border-white/10 bg-white/10 px-4 py-3 outline-none focus:ring-2 focus:ring-[#a783a6]"
                  >
                    <option value="">{t("search.filters.gender")}</option>
                    <option value="female">{t("search.filters.female")}</option>
                    <option value="male">{t("search.filters.male")}</option>
                    <option value="other">{t("search.filters.other")}</option>
                  </select>
                </div>

                <div>
                  <div className="mb-1.5 text-sm text-white/70">{t("search.filters.ratingFrom")}</div>
                  <select
                    value={minRating}
                    onChange={(e) => setMinRating(e.target.value)}
                    className="w-full rounded-2xl border border-white/10 bg-white/10 px-4 py-3 outline-none focus:ring-2 focus:ring-[#a783a6]"
                  >
                    <option value="">{t("search.filters.ratingFrom")}</option>
                    <option value="4">4+</option>
                    <option value="4.5">4.5+</option>
                    <option value="4.9">4.9+</option>
                  </select>
                </div>

                <div>
                  <div className="mb-1.5 text-sm text-white/70">{t("search.filters.maxPrice")}</div>
                  <input
                    type="number"
                    min="0"
                    value={maxRate}
                    onChange={(e) => setMaxRate(e.target.value)}
                    placeholder={t("search.filters.maxPrice")}
                    className="w-full rounded-2xl border border-white/10 bg-white/10 px-4 py-3 outline-none placeholder:text-white/40 focus:ring-2 focus:ring-[#a783a6]"
                  />
                </div>

                <div>
                  <div className="mb-1.5 text-sm text-white/70">{t("academic.degreeLabel")}</div>
                  <select value={degree} onChange={(e) => setDegree(e.target.value)} className="w-full rounded-2xl border border-white/10 bg-white/10 px-4 py-3 outline-none focus:ring-2 focus:ring-[#a783a6]">
                    <option value="">{t("academic.anyOption")}</option>
                    {DEGREE_KEYS.map((k) => <option key={k} value={k}>{t(`academic.degree.${k}`)}</option>)}
                  </select>
                </div>

                <div>
                  <div className="mb-1.5 text-sm text-white/70">{t("academic.titleLabel")}</div>
                  <select value={academicTitle} onChange={(e) => setAcademicTitle(e.target.value)} className="w-full rounded-2xl border border-white/10 bg-white/10 px-4 py-3 outline-none focus:ring-2 focus:ring-[#a783a6]">
                    <option value="">{t("academic.anyOption")}</option>
                    {TITLE_KEYS.map((k) => <option key={k} value={k}>{t(`academic.title.${k}`)}</option>)}
                  </select>
                </div>

                <div>
                  <div className="mb-1.5 text-sm text-white/70">{t("academic.experienceLabel")}</div>
                  <select value={expRange} onChange={(e) => setExpRange(e.target.value)} className="w-full rounded-2xl border border-white/10 bg-white/10 px-4 py-3 outline-none focus:ring-2 focus:ring-[#a783a6]">
                    <option value="">{t("academic.anyOption")}</option>
                    {EXPERIENCE_RANGES.map((r) => <option key={r.id} value={r.id}>{t(`academic.expRange.${r.id}`)}</option>)}
                  </select>
                </div>

                <label className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/10 px-4 py-3 text-sm text-white/85">
                  <input
                    type="checkbox"
                    checked={verifiedOnly}
                    onChange={(e) => setVerifiedOnly(e.target.checked)}
                    className="h-4 w-4 accent-[#98dea3]"
                  />
                  {t("credentials.filterVerifiedOnly")}
                </label>

              </div>

              <div className="flex gap-3 border-t border-white/10 p-4" style={{ paddingBottom: "max(env(safe-area-inset-bottom, 0px), 16px)" }}>
                <button
                  onClick={() => { resetFilters(); setFiltersOpen(false); }}
                  className="flex-1 rounded-2xl border border-white/15 bg-white/[0.05] py-3 text-sm font-medium text-white/80 transition active:bg-white/10"
                >
                  {t("search.reset")}
                </button>
                <button
                  onClick={() => setFiltersOpen(false)}
                  className="flex-[2] rounded-2xl bg-[#7269e3] py-3 font-semibold text-white transition hover:brightness-110"
                >
                  {t("search.showResults", { defaultValue: "Show results" })} ({results.length})
                </button>
              </div>
            </div>
          </>
        )}

        {loading ? (
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : loadError ? (
          <div className="mt-10 rounded-3xl bg-red-500/10 p-6 text-red-100 ring-1 ring-red-400/20">
            {loadError}
          </div>
        ) : results.length === 0 ? (
          <div className="mt-12 rounded-3xl bg-white/5 p-10 text-center ring-1 ring-white/10">
            <div className="text-2xl font-extrabold">{t("search.empty.title")}</div>
            <div className="mt-2 text-white/60">{t("search.empty.subtitle")}</div>

            <div className="mt-5 flex flex-col justify-center gap-3 sm:flex-row">
              <button
                onClick={resetFilters}
                className="rounded-2xl border border-white/15 bg-white/[0.04] px-5 py-3 text-sm text-white/80 transition hover:bg-white/[0.07]"
              >
                {t("search.empty.reset")}
              </button>

              <button
                onClick={openModal}
                className="rounded-2xl bg-[#7269e3] px-5 py-3 font-semibold transition hover:brightness-110"
              >
                {t("search.createRequest")}
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {results.map((tu) => (
              <TutorCard key={tu.id} tutor={tu} />
            ))}
          </div>
        )}
      </div>

      {showGuestGate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 px-4 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-3xl bg-[#1f2430] p-6 text-center shadow-2xl ring-1 ring-white/10">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#7269e3]/20 text-2xl">
              🔐
            </div>
            <p className="font-semibold text-white">{t("search.guestGate.title")}</p>
            <p className="mt-2 text-sm text-white/65">{t("search.guestGate.body")}</p>

            <Link
              to={`/signup?next=${encodeURIComponent("/search")}`}
              onClick={() => setShowGuestGate(false)}
              className="mt-5 block w-full rounded-xl bg-[#7269e3] px-4 py-2.5 text-center font-semibold text-white shadow-[0_4px_14px_rgba(114,105,227,0.4)] hover:brightness-110"
            >
              {t("search.guestGate.signup")}
            </Link>

            <p className="mt-3 text-sm text-white/50">
              {t("search.guestGate.haveAccount")}{" "}
              <Link
                to={`/login?next=${encodeURIComponent("/search")}`}
                onClick={() => setShowGuestGate(false)}
                className="text-[#98dea3] hover:underline"
              >
                {t("search.guestGate.login")}
              </Link>
            </p>

            <button
              onClick={() => setShowGuestGate(false)}
              className="mt-4 w-full rounded-xl bg-white/5 px-4 py-2.5 text-sm text-white/70 transition hover:bg-white/10"
            >
              {t("common.cancel")}
            </button>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/75 px-4 py-6 backdrop-blur-sm">
          <div className="flex min-h-full items-start justify-center">
            <div className="max-h-[85vh] w-full max-w-2xl overflow-hidden rounded-3xl bg-[#1f2430] shadow-2xl ring-1 ring-white/10">
              <div className="flex items-start justify-between gap-4 border-b border-white/10 p-6">
                <div>
                  <h2 className="font-unbounded text-2xl font-extrabold tracking-tight">
                    {t("search.modal.title")}
                  </h2>

                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-sm text-white/70">
                      {t("search.modal.preview")}
                    </span>
                    <span className="inline-flex items-center rounded-full bg-[#7269e3]/20 px-3 py-1 text-sm font-semibold ring-1 ring-[#7269e3]/40">
                      {previewRecipients.length}
                    </span>
                  </div>
                </div>

                <button
                  onClick={() => setShowModal(false)}
                  className="shrink-0 rounded-2xl bg-white/10 px-3 py-2 text-white/80 transition hover:bg-white/15"
                  aria-label={t("common.close")}
                >
                  ✕
                </button>
              </div>

              <div className="max-h-[calc(85vh-88px)] overflow-y-auto p-6">
                <div className="grid gap-4 md:grid-cols-2">
                  {/* Step 1: Sphere */}
                  <Field label={t("search.modal.sphere")}>
                    <SphereSelect
                      value={reqSphere}
                      onChange={(v) => { setReqSphere(v); setReqSubject(""); }}
                      t={t}
                      placeholder={t("search.modal.spherePh")}
                      className="w-full rounded-2xl border border-white/10 bg-white/10 px-4 py-2.5 outline-none focus:ring-2 focus:ring-[#7269e3]"
                    />
                  </Field>

                  {/* Step 2: Specialization — only shown after sphere selected */}
                  {reqSphere ? (
                    <Field label={t("search.modal.specialization")}>
                      <SubjectSelect
                        value={reqSubject}
                        onChange={setReqSubject}
                        sphere={reqSphere}
                        t={t}
                        extra={[]}
                        className="w-full rounded-2xl border border-white/10 bg-white/10 px-4 py-2.5 outline-none focus:ring-2 focus:ring-[#7269e3]"
                      />
                    </Field>
                  ) : (
                    <div className="flex items-center rounded-2xl border border-white/5 bg-white/[0.03] px-4 py-2.5 text-sm text-white/35">
                      {t("search.modal.specializationHint")}
                    </div>
                  )}

                  {/* Format field removed — platform is online-only */}

                  <Field label={t("search.filters.gender")}>
                    <select
                      value={reqGender}
                      onChange={(e) => setReqGender(e.target.value)}
                      className="w-full rounded-2xl border border-white/10 bg-white/10 px-4 py-2.5 outline-none focus:ring-2 focus:ring-[#7269e3]"
                    >
                      <option value="">{t("search.modal.anyGender")}</option>
                      <option value="female">{t("search.filters.female")}</option>
                      <option value="male">{t("search.filters.male")}</option>
                      <option value="other">{t("search.filters.other")}</option>
                    </select>
                  </Field>

                  <Field label={t("search.filters.ratingFrom")}>
                    <select
                      value={reqMinRating}
                      onChange={(e) => setReqMinRating(e.target.value)}
                      className="w-full rounded-2xl border border-white/10 bg-white/10 px-4 py-2.5 outline-none focus:ring-2 focus:ring-[#7269e3]"
                    >
                      <option value="">{t("search.modal.anyRating")}</option>
                      <option value="4">4+</option>
                      <option value="4.5">4.5+</option>
                      <option value="4.9">4.9+</option>
                    </select>
                  </Field>

                  <Field label={t("search.modal.ageFrom")}>
                    <input
                      type="number"
                      value={reqMinAge}
                      onChange={(e) => setReqMinAge(e.target.value)}
                      placeholder={t("search.modal.ageFromPlaceholder")}
                      className="w-full rounded-2xl border border-white/10 bg-white/10 px-4 py-2.5 outline-none focus:ring-2 focus:ring-[#7269e3]"
                    />
                  </Field>

                  <Field label={t("search.modal.ageTo")}>
                    <input
                      type="number"
                      value={reqMaxAge}
                      onChange={(e) => setReqMaxAge(e.target.value)}
                      placeholder={t("search.modal.ageToPlaceholder")}
                      className="w-full rounded-2xl border border-white/10 bg-white/10 px-4 py-2.5 outline-none focus:ring-2 focus:ring-[#7269e3]"
                    />
                  </Field>
                </div>

                <div className="mt-5">
                  <Field label={t("search.modal.budget")}>
                    <input
                      type="number"
                      value={taskBudget}
                      onChange={(e) => setTaskBudget(e.target.value)}
                      placeholder={t("search.modal.budgetPlaceholder")}
                      className="w-full rounded-2xl border border-white/10 bg-white/10 px-4 py-2.5 outline-none focus:ring-2 focus:ring-[#a783a6]"
                    />
                  </Field>
                </div>

                <div className="mt-4">
                  <Field label={t("search.modal.describeTask")}>
                    <textarea
                      rows={5}
                      value={taskMessage}
                      onChange={(e) => setTaskMessage(e.target.value)}
                      placeholder={t("search.modal.messagePlaceholder")}
                      className="w-full resize-y rounded-2xl border border-white/10 bg-white/10 px-4 py-3 outline-none focus:ring-2 focus:ring-[#a783a6]"
                    />
                  </Field>
                </div>

                <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                  <button
                    onClick={() => setShowModal(false)}
                    className="rounded-2xl bg-white/10 px-4 py-2.5 text-white/90 transition hover:bg-white/15"
                  >
                    {t("common.cancel")}
                  </button>

                  <button
                    disabled={sending}
                    onClick={submitBroadcast}
                    className="rounded-2xl bg-[#98dea3] px-5 py-2.5 font-extrabold text-black transition hover:brightness-105 disabled:opacity-60"
                  >
                    {sending ? t("search.modal.sending") : t("search.modal.submit")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
